import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { deliveryTrackingApi, DeliveryTrackingResponse } from '../services/api';

/** Passos exibidos na linha do tempo, em ordem. */
const STEPS: { key: string; label: string }[] = [
  { key: 'PREPARANDO', label: 'Preparando' },
  { key: 'SAIU_PARA_ENTREGA', label: 'Saiu para entrega' },
  { key: 'CHEGANDO', label: 'Chegando' },
  { key: 'ENTREGUE', label: 'Entregue' },
];

/**
 * Acompanhamento público da entrega (#305). Aberto pelo link com token; sem
 * login. Faz polling do backend (o RTDB fica protegido; o backend é o proxy).
 */
const TrackingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<DeliveryTrackingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await deliveryTrackingApi.track(token);
      setData(res);
      setError(null);
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError('Rastreio não encontrado ou expirado.');
      } else {
        setError('Não foi possível carregar o rastreio agora.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 15000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [load]);

  const cancelled = data?.step === 'CANCELADA';
  const currentIdx = data ? STEPS.findIndex((s) => s.key === data.step) : -1;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: 20, fontFamily: 'system-ui, sans-serif' }}>
      <h2 style={{ marginBottom: 4 }}>Acompanhar entrega</h2>
      {data?.orderDisplayId && (
        <div style={{ color: '#666', marginBottom: 16 }}>Pedido #{data.orderDisplayId}</div>
      )}

      {loading && <div>Carregando…</div>}
      {!loading && error && <div style={{ color: '#c62828' }}>{error}</div>}

      {!loading && !error && data && (
        <>
          {cancelled ? (
            <div style={{ background: '#fdecea', border: '1px solid #f5c6cb', borderRadius: 10, padding: 14, color: '#c62828' }}>
              {data.stepLabel}. Fale com a loja para mais detalhes.
            </div>
          ) : (
            <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {STEPS.map((s, i) => {
                const done = currentIdx >= 0 && i <= currentIdx;
                const active = i === currentIdx;
                return (
                  <li key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0' }}>
                    <span
                      style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: done ? '#2e7d32' : '#e0e0e0',
                        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13,
                      }}
                    >
                      {done ? '✓' : i + 1}
                    </span>
                    <span style={{ fontWeight: active ? 700 : 500, color: done ? '#111' : '#999' }}>
                      {s.label}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}

          {!cancelled && data.step !== 'ENTREGUE' && (
            <div style={{ marginTop: 16 }}>
              {data.driverFirstName && (
                <div style={{ marginBottom: 6 }}>
                  Entregador: <strong>{data.driverFirstName}</strong>
                </div>
              )}
              {typeof data.etaMinutes === 'number' && (
                <div style={{ marginBottom: 6 }}>Chega em ~{data.etaMinutes} min</div>
              )}
              {data.driverPosition && (
                <div style={{ color: '#2e7d32' }}>📍 Entregador a caminho — posição sendo atualizada.</div>
              )}
            </div>
          )}

          {data.confirmationCode && data.step !== 'ENTREGUE' && (
            <div style={{ marginTop: 20, background: '#f1f8e9', border: '1px solid #c5e1a5', borderRadius: 10, padding: 16, textAlign: 'center' }}>
              <div style={{ color: '#555', fontSize: 13 }}>Informe este código ao entregador</div>
              <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: 8 }}>{data.confirmationCode}</div>
            </div>
          )}

          {data.step === 'ENTREGUE' && (
            <div style={{ marginTop: 20, color: '#2e7d32', fontWeight: 700 }}>
              Pedido entregue{data.deliveredAt ? ` às ${new Date(data.deliveredAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}. Bom apetite!
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrackingPage;
